import { useState } from "react";
import Head from "next/head";
import getConfig from "next/config";
import { connect } from "react-redux";
import classNames from "classnames";

import HomepageSlider from "@components/HomepageSlider";
import OrganizationSchema from "@components/OrganizationSchema";
import Tabs, { Tab } from "@components/Tabs";
import ExpandableProductList from "@components/ExpandableProductList";
import Heading from "@components/Heading";
import { trackProductClick } from "@store/tracker/actions";
import { getCatalogPromotions, getPage } from "@store/pages/actions";
import { getCmsBlocks, getWebsiteAdminConfig } from "@store/app/actions";
import CompareWidget from "@containers/Catalog/CompareWidget";
import PromoToasts from "@components/PromoToasts";
import ShopInShopBanners from "@components/ShopInShopBanners";
import { apiCall } from "@store/app/actions";
import { getBannersV2Api } from "@lib/api";
import FootprintsWebAdsBanner from "@components/Footprints/FootprintsWebAdsBanner";
import transformV2Banners from "@lib/transformV2Banners";
import FootprintsHomepageSlider from "@components/Footprints/FootprintsHomepageSlider";
import CredexUserPromoBanner from "@components/CredexUserPromoBanner";

const {
  publicRuntimeConfig: { settings, content },
} = getConfig();

// TODO Before live deploy we must set live banners ID
// TODO Before live deploy we must set live banners ID
// TODO Before live deploy we must set live banners ID

const BANNERS_IDS = {
  altex: {
    shopInShop: {
      prod: "adebe594-6419-440d-8110-ce6b1dc6d9ef",
      staging: "d49cc0b2-ed17-47ef-be80-2f3d53b7c1f4",
    },
    hero: {
      prod: 51,
      staging: 51,
    },
  },
  mediagalaxy: {
    shopInShop: {
      prod: "6b7f2f4c-e99c-42a1-b361-542e8bbc12bb",
      staging: "d49cc0b2-ed17-47ef-be80-2f3d53b7c1f4",
    },
    hero: {
      prod: 52,
      staging: 52,
    },
  },
  bricostore: {
    shopInShop: {
      prod: "a363e6b8-4bb6-4f03-bcb5-9ede21772d4b",
      staging: "a363e6b8-4bb6-4f03-bcb5-9ede21772d4b",
    },
    hero: {
      prod: "80d1e07a-9742-4653-bd05-b65659e10828",
      staging: "80d1e07a-9742-4653-bd05-b65659e10828",
    },
  },
};

const Home = ({
  pageRes,
  promotions,
  shopInShopBanners,
  heroBanners,
  trackProductClick,
  layoutProps,
  featureFlags,
  customer,
}) => {
  const asyncPageInternalAnchors = {
    promoted: "oferte-zilnice",
    containers: "oferte",
    discounted: "promotii",
  };
  const alternateIndex =
    !!layoutProps &&
    !!layoutProps.urlAsPath &&
    layoutProps.urlAsPath.indexOf("/home/") === 0;

  // The footprints slider takes the hero slot only once it has actually
  // rendered an ad. Its content API can answer with no content at all, and its
  // campaigns are matched against the exact page URL, so the slot stays empty
  // on /home/ and on any URL carrying query params. Until slides show up, the
  // CMS banners stay on screen instead of leaving the hero blank.
  const [hasFootprintsSlides, setHasFootprintsSlides] = useState(false);

  const showFootprintsSlider = Boolean(
    featureFlags?.enableFootprints &&
      featureFlags?.enableHomepageFootprintsSlider
  );

  const showHeroBanners = Boolean(heroBanners) && !hasFootprintsSlides;

  const showFootprintsBanner = Boolean(
    featureFlags?.enableFootprints && featureFlags?.enableFootprintsBanner
  );

  const handleProductClick = (product, data) => {
    // Track product click
    trackProductClick(product, data);
  };

  const hasCredexUserPromo = customer?.has_promo === "CC12RATE";

  return (
    <>
      <Head>
        <title>{(!!pageRes && pageRes.title) || content.websiteTitle}</title>

        {alternateIndex ? (
          <link rel="alternate" href={settings.baseUrl} />
        ) : (
          <link rel="canonical" href={settings.baseUrl} />
        )}
        {!!pageRes && !!pageRes.description && (
          <meta name="description" content={pageRes.description} />
        )}
        {!!pageRes && !!pageRes.keywords && (
          <meta name="keywords" content={pageRes.keywords} />
        )}
      </Head>

      <OrganizationSchema />

      {showFootprintsSlider && (
        <FootprintsHomepageSlider onSlidesChange={setHasFootprintsSlides} />
      )}

      {showHeroBanners && (
        <>
          {heroBanners.images_mobile.length > 0 && (
            <div className="sm:hidden">
              <HomepageSlider items={heroBanners.images_mobile} key="1" />
            </div>
          )}

          {heroBanners.images_tablet.length > 0 && (
            <div className="hidden sm:block lg:hidden">
              <HomepageSlider
                items={heroBanners.images_tablet}
                autoplay
                key="2"
              />
            </div>
          )}

          {heroBanners.images.length > 0 && (
            <div className="hidden lg:block">
              <HomepageSlider items={heroBanners.images} autoplay key="3" />
            </div>
          )}
        </>
      )}

      <div>
        {shopInShopBanners && (
          <div className="container pt-2">
            <ShopInShopBanners
              banners={shopInShopBanners.map((banner, index) => ({
                ...banner,
                index: index + 1,
              }))}
            />
          </div>
        )}

        {hasCredexUserPromo && (
          <div className="container mt-4">
            <CredexUserPromoBanner />
          </div>
        )}

        {promotions.top_offer && (
          <div
            id={asyncPageInternalAnchors.promoted}
            className="mt-3 container"
          >
            <Heading
              as="h2"
              display="h2"
              title="Top Oferte"
              classesProp="lg:mb-4 mt-28px lg:mt-12 lg:-tracking-0.67 -tracking-0.5"
            />

            <ExpandableProductList
              products={promotions.top_offer.map((product, index) => ({
                ...product,
                index: index + 1,
                item_list_name: "top oferte",
              }))}
              desktopVisibleProducts={10}
              onProductClick={(product) => handleProductClick(product)}
            />
          </div>
        )}

        {promotions.top_offer_tabs && (
          <div
            className={classNames("mt-20 md:px-10 md:pt-12 md:pb-6 md:mb-16", {
              "bg-gray-100": settings.websiteName !== "bricostore",
              "bg-gray-lighter": settings.websiteName === "bricostore",
            })}
            id={asyncPageInternalAnchors.containers}
          >
            <Tabs>
              {promotions.top_offer_tabs.map((container, index) => (
                <Tab
                  selected={index === 0 && true}
                  key={`${container.tab_name}-${index}`}
                  title={container.tab_name}
                >
                  <div className="mt-6 mb-10 mx-4 lg:mx-0">
                    <ExpandableProductList
                      products={container.products.map((product, index) => ({
                        ...product,
                        index: index + 1,
                        item_list_name: "oferta zilei",
                      }))}
                      desktopVisibleProducts={5}
                      onProductClick={(product) => handleProductClick(product)}
                    />
                  </div>
                </Tab>
              ))}
            </Tabs>
          </div>
        )}

        <div className="container">
          {promotions.promotions && (
            <div id={asyncPageInternalAnchors.discounted} className="mb-6">
              <Heading
                as="h2"
                display="h2"
                title="Promotii"
                classesProp="mt-28px lg:my-3 lg:-tracking-0.67 -tracking-0.5"
              />

              <ExpandableProductList
                products={promotions.promotions.map((product, index) => ({
                  ...product,
                  index: index + 1,
                  item_list_name: "promotii",
                }))}
                desktopVisibleProducts={10}
                onProductClick={(product) => handleProductClick(product)}
              />
            </div>
          )}

          {/* <div
            className={classNames({
              "mt-8": settings.websiteName === "mediagalaxy",
            })}
          >
            <div className="hidden sm:block">
              <Newsletter type="banner" isHomePage={true} />
            </div>

            <div className="sm:hidden">
              <Newsletter type="hero" isHomePage={true} />
            </div>
          </div> */}
        </div>

        <CompareWidget />
      </div>

      <PromoToasts type="homepage" />

      {showFootprintsBanner && (
        <div className="container">
          <FootprintsWebAdsBanner desktopSize="970x250" mobileSize="300x250" />
        </div>
      )}
    </>
  );
};

Home.getInitialProps = async (ctx) => {
  const { reduxStore } = ctx;

  const pageProps = {
    pageRes: null,
    promotions: {},
    shopInShopBanners: null,
    heroBanners: null,
  };

  const pageRes = await reduxStore.dispatch(getPage("home"));

  await reduxStore.dispatch(getCmsBlocks(["altex_catalog_product_services"]));

  const pageBanners = await reduxStore.dispatch(
    apiCall({
      apiCall: getBannersV2Api,
      formatResponse: (response) => response.data,
    })
  );

  const pageRequests = [
    // 7 as payload will fetch all promotions categories
    reduxStore.dispatch(getCatalogPromotions(7)),
  ];

  // The feature flags decide whether the footprints slider is mounted, so the
  // request has to settle before the page renders. Left floating, the server
  // render raced it and the same URL came back with or without the flags in
  // the store, with the page cache then freezing whichever variant won.
  if (!reduxStore.getState().app.websiteAdminConfigs.featureFlagsWebsite) {
    pageRequests.push(
      reduxStore.dispatch(
        getWebsiteAdminConfig({ code: "featureFlagsWebsite" })
      )
    );
  }

  const [pagePromotions] = await Promise.all(pageRequests);

  if (pageRes) {
    const { content, ...rest } = pageRes;
    pageProps.pageRes = rest;
  }

  if (pagePromotions?.data) {
    if (pagePromotions.data.top_offer?.length > 0) {
      pageProps.promotions.top_offer = pagePromotions.data.top_offer;
    }

    if (pagePromotions.data.promotions?.length > 0) {
      pageProps.promotions.promotions = pagePromotions.data.promotions;
    }

    const topOffersTabsWithProducts =
      pagePromotions.data.top_offer_tabs?.filter(
        (tab) => tab.products?.length > 0
      );

    if (topOffersTabsWithProducts?.length > 0) {
      pageProps.promotions.top_offer_tabs = topOffersTabsWithProducts;
    }
  }

  const shopInShopBanners = pageBanners?.items?.find(
    (item) =>
      item.id ==
      BANNERS_IDS[settings.websiteName].shopInShop[
        settings.isProd && !settings.isStaging ? "prod" : "staging"
      ]
  );

  const heroBanners = pageBanners?.items?.find(
    (item) =>
      item.id ==
      BANNERS_IDS[settings.websiteName].hero[
        settings.isProd && !settings.isStaging ? "prod" : "staging"
      ]
  );

  if (shopInShopBanners?.images?.length > 0) {
    pageProps.shopInShopBanners = shopInShopBanners.images;
  }

  if (heroBanners?.images?.length > 0) {
    pageProps.heroBanners = transformV2Banners(heroBanners);
  }

  return pageProps;
};

Home.pageLayout = "default";

export default connect(
  (state) => ({
    cmsBlocks: state.app.cmsBlocks,
    customer: state.user.customer,
    featureFlags: state.app.websiteAdminConfigs.featureFlagsWebsite,
  }),
  {
    trackProductClick,
    getPage,
  }
)(Home);
